import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import DynamicFormField from "./DynamicFormField";
import { journeyActions } from "../../store/journeySlice";
import { validateForm } from "../utils";


const INDIA_RESIDENT_FIELDS = [
  "basedInIndia",
  "nationality",
  "nriResident",
  "isIndiaResident",
  "areYouBasedInIndia",
];
const isIndiaResidentField = (name) => INDIA_RESIDENT_FIELDS.includes(name);
const isNoValue = (v) =>
  v &&
  ["no", "false", "nri", "non-resident"].includes(
    String(v).toLowerCase().trim(),
  );

const EditStepModal = ({
  isOpen,
  onClose,
  stepIndex = null,
  title = "Edit Details",
  onSave,
  className,
  formData,
  disabledFieldNames = [],
  disableAllFields = false,
}) => {
  const dispatch = useDispatch();
  const { formDefinition } = useSelector((s) => s.journey);
  const [editData, setEditData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [, setSubmitError] = useState("");
  const modalRef = useRef(null);
  const [, setShowAttentionModal] = useState(false);
  const attentionModalRevertRef = useRef(null);

  const resolveFieldValue = useCallback((fieldName) => {
    if (!fieldName || !formData) return "";
    if (formData[fieldName] !== undefined && formData[fieldName] !== null) {
      return formData[fieldName];
    }

    const aliasMap = {
      fullName: ["lifeAssuredName", "name", "proposerName"],
      lifeAssuredName: ["fullName", "name", "proposerName"],
      dateOfBirth: ["lifeAssuredDob", "dob", "proposerDob"],
      lifeAssuredDob: ["dateOfBirth", "dob", "proposerDob"],
      mobileNumber: ["mobile", "mobileNo", "liMobileno"],
      emailAddress: ["emailId", "email", "liEmailid"],
      residencyPinCode: ["pinCode", "pincode"],
      pinCode: ["residencyPinCode", "pincode"],
      gender: ["lifeAssuredGender", "proposerGender", "liGender"],
      lifeAssuredGender: ["gender", "proposerGender", "liGender"],
      occupation: ["occupationClass"],
      educationalLevel: ["education"],
      tobaccoConsumption: ["smokerStatus", "smokingHabit", "liSmoke"],
    };

    const aliases = aliasMap[fieldName] || [];
    for (const alias of aliases) {
      if (formData[alias] !== undefined && formData[alias] !== null) {
        return formData[alias];
      }
    }
    return "";
  }, [formData]);

  useEffect(() => {
    if (!isOpen || !formDefinition?.fieldGroups) return;

    const stepFields =
      stepIndex !== null
        ? formDefinition.fieldGroups[stepIndex]?.fields || []
        : formDefinition.fieldGroups.flatMap((g) => g.fields || []);

    const stepData = {};
    stepFields.forEach((field) => {
      const fieldName = field.fieldReference || field.name || field.fieldName;
      stepData[fieldName] = resolveFieldValue(fieldName);
    });

    setEditData(stepData);
    setErrors({});
  }, [isOpen, stepIndex, formDefinition, resolveFieldValue]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && !saving) onClose();
    },
    [onClose, saving],
  );

  if (!isOpen || !formDefinition?.fieldGroups) return null;

  const fields =
    stepIndex !== null
      ? formDefinition.fieldGroups[stepIndex]?.fields || []
      : formDefinition.fieldGroups.flatMap((g) => g.fields || []);

    const handleChange = (e) => {
    const { name, value } = e.target;
    if (!name) return;
    const willShowAttention = isIndiaResidentField(name) && isNoValue(value);
    if (willShowAttention) {
      attentionModalRevertRef.current = {
        fieldName: name,
        previousValue: editData?.[name],
      };
    }
    setEditData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (willShowAttention) {
      setShowAttentionModal(true);
    }
  };

  const handleSave = async () => {
  const fieldErrors = validateForm(editData, fields);

  if (Object.keys(fieldErrors).length > 0) {
    setErrors(fieldErrors);
    return;
  }
  dispatch(journeyActions.setLeadFormData(editData));
  setSubmitError("");
  setSaving(true);

  if (typeof onSave === "function") {
    try {
      const result = await onSave(editData, stepIndex);
        onClose();
      if (result === false) return;
    } finally {
      setSaving(false);
    }
  }
  onClose();
};
  const disabledFieldNameSet = new Set(disabledFieldNames || []);

  return (
    <div
      className={`edit-modal-backdrop ${className}`}
      onClick={saving ? undefined : onClose}
    >
      <div
        ref={modalRef}
        className={`edit-modal-container edit-step-modal ${stepIndex === 0 ? 'basic-edit-modal' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-modal-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="edit-modal-header">
          <h5 className="edit-modal-title light" id="edit-modal-title">
            {title}
          </h5>
          <button
            className="edit-modal-close"
            onClick={saving ? undefined : onClose}
            disabled={saving}
            aria-label="Close"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                fill="#3C3D3D"
              />
            </svg>
          </button>
        </div>
        <div className="edit-modal-contents edit-step-modal-fields">
          {fields.map((field, i) => {
            const fieldName =
              field.fieldReference || field.name || field.fieldName;
            return (
              <DynamicFormField
                key={fieldName || i}
                field={field}
                value={editData[fieldName]}
                onChange={handleChange}
                error={errors[fieldName] || null}
                disabled={disableAllFields || disabledFieldNameSet.has(fieldName)}
                useDdMmYyyyForDob
              />
            );
          })}
        </div>
       <div className="edit-modal-footer">
          <button
            className="primary-button btn regular "
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : disableAllFields ? 'Okay' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditStepModal;
